import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { getTenantId, getDepartmentId } from 'src/app/services/store-snapshot.service';
import { ALL_DEPS_TOKEN } from 'src/app/tenant-selector/state/tenant-user-access.state';
import {
  MainTableFilterModel,
  MainTableFilterSearchByModel,
} from 'src/app/shared/models/main-table-filter.model';
import { ApiResponse, ApiResponseWithLocations } from 'src/app/core/models/api-response';
import { MainTableFilteringHelperService } from 'src/app/shared/main-table-module/services/main-table-filtering-helper.service';
import { environment } from '../../../environments/environment';
import { IDueListItem } from '../models/due-list-item.model';
import { IDueListPrintRequest } from '../models/due-list-print-request.model';
import { IDueListCounts } from '../models/due-list-counts.model';

const COLUMN_TO_KEY_MAP: Record<string, string> = {
  type: 'Category',
  department: 'DepartmentName',
  responsible: 'Responsibles',
  placement: 'Location',
  expires: 'Expires',
  daysUntilExpired: 'DaysUntilExpired',
  status: 'Status',
};
@Injectable({
  providedIn: 'root',
})
export class DueListApiService {
  headers = { 'content-type': 'application/json' };

  private get url(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/${getDepartmentId()}/dueList`;
  }
  private get dueListUrl(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/dueList`;
  }

  constructor(private http: HttpClient) {}

  getDueListWarnings(
    currentFilter?: MainTableFilterModel
  ): Observable<ApiResponseWithLocations<IDueListItem[]>> {
    const depId = getDepartmentId();
    if (depId === null || depId === undefined || depId === '') {
      return of();
    }
    const url = `${this.url}/warnings`;
    let params = new HttpParams();
    if (currentFilter) {
      params = params.append('Page', currentFilter.pageIndex.toString());
      params = params.append('PageSize', currentFilter.pageSize.toString());
    }
    if (currentFilter?.searchTerm) {
      params = params.append('GlobalSearch', currentFilter.searchTerm);
    }
    if (currentFilter?.orderBy && currentFilter.ascDesc) {
      params = params.append(
        `OrderBy[${COLUMN_TO_KEY_MAP[currentFilter.orderBy] || currentFilter.orderBy}]`,
        currentFilter.ascDesc
      );
    }
    params = MainTableFilteringHelperService.appendFilteringParams(
      params,
      currentFilter,
      COLUMN_TO_KEY_MAP
    );
    return this.http.get<ApiResponseWithLocations<IDueListItem[]>>(url, { params });
  }

  getDueListDueCompetences(
    currentFilter?: MainTableFilterModel
  ): Observable<ApiResponse<IDueListItem[]>> {
    let url = `${this.dueListUrl}/competences`;
    if (currentFilter) {
      url = `${url}?Page=${currentFilter.pageIndex}&PageSize=${currentFilter.pageSize}`;
    }
    if (currentFilter?.searchTerm) {
      url = `${url}&GlobalSearch=${currentFilter.searchTerm}`;
    }
    return this.http.get<ApiResponse<IDueListItem[]>>(url);
  }

  getMyDueListWarnings(
    currentFilter?: MainTableFilterModel
  ): Observable<ApiResponseWithLocations<IDueListItem[]>> {
    const depId = getDepartmentId();
    if (depId === null || depId === undefined || depId === '') {
      return of();
    }
    const url = `${this.dueListUrl}/myWarnings`;
    let params = new HttpParams();
    if (currentFilter) {
      params = params.append('Page', currentFilter.pageIndex.toString());
      params = params.append('PageSize', currentFilter.pageSize.toString());
    }
    if (currentFilter?.searchTerm) {
      params = params.append('GlobalSearch', currentFilter.searchTerm);
    }
    if (currentFilter?.orderBy && currentFilter.ascDesc) {
      params = params.append(
        `OrderBy[${COLUMN_TO_KEY_MAP[currentFilter.orderBy] || currentFilter.orderBy}]`,
        currentFilter.ascDesc
      );
    }
    params = MainTableFilteringHelperService.appendFilteringParams(
      params,
      currentFilter,
      COLUMN_TO_KEY_MAP
    );
    return this.http.get<ApiResponseWithLocations<IDueListItem[]>>(url, { params });
  }

  print(request?: Partial<IDueListPrintRequest>): Observable<Blob> {
    const currentDepartmentId = getDepartmentId();
    let printUrl = '';
    if (currentDepartmentId === ALL_DEPS_TOKEN) {
      printUrl = `${this.dueListUrl}/MyWarnings/print/pdf`;
    } else {
      printUrl = `${this.url}/warnings/print/pdf`;
    }
    const searchBy: Record<string, string> = {};
    const groupedSearchBy = (request?.filter?.searchBy || []).reduce((acc, curr) => {
      if (!acc[curr.property]) {
        acc[curr.property] = [];
      }
      acc[curr.property].push(curr);
      return acc;
    }, {} as Record<string, MainTableFilterSearchByModel[]>);

    Object.keys(groupedSearchBy).forEach((property) => {
      searchBy[COLUMN_TO_KEY_MAP[property] || property] = JSON.stringify(
        groupedSearchBy[property].reduce((acc, curr) => {
          acc[curr.operand] = curr.value;
          return acc;
        }, {} as Record<string, any>)
      );
    });
    const body = {
      recordsToPrint: request?.recordsToPrint || [],
      categoriesToPrint: request?.categoriesToPrint || [],
      searchBy,
    };

    return this.http.post(printUrl, body, { responseType: 'blob' });
  }

  getDueListCounts(): Observable<IDueListCounts> {
    let { url } = this;
    const depId = getDepartmentId();
    if (depId === ALL_DEPS_TOKEN) {
      url = this.dueListUrl;
    }
    url = `${url}/all/counts`;
    return this.http.get<IDueListCounts>(url);
  }
}
